/**
 * useConfirmDialog
 *
 * A hook that provides a React-rendered confirmation dialog using the new Modal
 * component with ModalStackProvider support. Replaces the legacy redux-driven
 * showConfirmModal/CONFIRM_MODAL pattern.
 *
 * Usage:
 *   const { confirm, dialog } = useConfirmDialog();
 *
 *   // Trigger the dialog:
 *   confirm({
 *     title: "Discard post?",
 *     message: "Any unsaved work will be lost.",
 *     confirmText: "Discard post",
 *     onConfirm: () => { dispatch(closeForm()); },
 *   });
 *
 *   // Render in JSX:
 *   return <>{dialog}</>;
 */
import React, { useCallback, useState } from "react";
import { Modal } from "@/components/modal/modal";
import { Button } from "@/components/ui/button";

export interface ConfirmDialogOptions {
  /** Dialog title (e.g. "Discard post?") */
  title: string;
  /** Body message (string or JSX) */
  message: React.ReactNode;
  /** Text for the destructive/confirm button. Default: "Confirm" */
  confirmText?: string;
  /** Text for the keep-editing/cancel button. Default: "Keep editing" */
  cancelText?: string;
  /** Icon class for the centered icon. Default: "fa-solid fa-trash-can" */
  icon?: string;
  /** Background color class for the icon circle. Default: "bg-red-100" */
  iconBg?: string;
  /** Icon color class. Default: "text-red-500" */
  iconColor?: string;
  /** Button variant for the confirm button. Default: "red-primary" */
  confirmVariant?: "red-primary" | "blue-primary" | "blue-secondary";
  /** Callback when the user confirms the destructive action */
  onConfirm: () => void;
}

export function useConfirmDialog() {
  const [state, setState] = useState<ConfirmDialogOptions | null>(null);

  const confirm = useCallback((options: ConfirmDialogOptions) => {
    setState(options);
  }, []);

  const close = useCallback(() => {
    setState(null);
  }, []);

  const handleConfirm = useCallback(() => {
    state?.onConfirm();
    setState(null);
  }, [state]);

  const dialog = state ? (
    <Modal show={true} onHide={close} className="max-w-[440px]" keyboard={true}>
      <div className="flex flex-col items-center text-center">
        <div
          className={`w-10 h-10 rounded-full ${state.iconBg || "bg-red-100"} flex items-center justify-center mb-3`}
        >
          <i
            className={`${state.icon || "fa-solid fa-trash-can"} ${state.iconColor || "text-red-500"} text-base`}
          />
        </div>
        <h4 className="text-base font-semibold text-neutral-900 mb-1">
          {state.title}
        </h4>
        <p className="font-inter text-sm text-neutral-600 mb-0">
          {state.message}
        </p>
      </div>
      <div className="flex items-center justify-end gap-3">
        <Button variant="blue-secondary" size="md" onClick={close}>
          {state.cancelText || "Keep editing"}
        </Button>
        <Button
          variant={state.confirmVariant || "red-primary"}
          size="md"
          onClick={handleConfirm}
        >
          {state.confirmText || "Confirm"}
        </Button>
      </div>
    </Modal>
  ) : null;

  return { confirm, close, dialog, isOpen: !!state };
}
